(ns minorodata.components.forms.warehouse
  (:require
   [applied-science.js-interop :as js-interop]
   [clojure.string]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.components.spinner.core :as components.spinner]
   [re-posh.core :as re-posh]
   [react :as react]
   [ui.fields]))


(defn convert-select-option [option]
  (cond
   (string? option) {:value option :text option :data-cy (str "select-option-" option)}
   (map? option)    {:value (str (:value option)) :text (:name option) :data-cy (or (:data-cy option) (str "select-option-" (:value option)))}
   :else            option))


(defn maybe-add-existing-option [options existing]
  (if (and (some? existing)
           (= (.indexOf options existing) -1))
    (conj options existing)
    options))

(defn destination-select' [{:keys [block field errors loading catalogs schemas default-catalog]}]
  (let [{:keys [loading-text empty-catalogs-text empty-schemas-text]
         :or   {loading-text        "Loading"
                empty-catalogs-text "No databases to select"
                empty-schemas-text  "No schemas to select"}} block

        [table schema catalog] (some-> (:value field)
                                       (clojure.string/split #"\.")
                                       (reverse))

        init                    (react/useRef false)
        [catalog set-catalog]   (react/useState (or catalog default-catalog))
        [schema set-schema]     (react/useState schema)
        [table set-table]       (react/useState table)
        field-action    (fn [catalog schema table]
                          (let [destination     (str catalog "." schema "." table)
                                original-action (:action field)]
                            (when (and (some? catalog) (some? schema) (some? table))
                              (original-action destination))))]

    ;; Initial setup

    (react/useEffect
     (fn []
       (dispatch [:warehouse-meta/add {}])
       (dispatch [:connections.api/fetch-warehouse-meta {:object :catalogs}])
       (when (some? catalog)
         (dispatch [:connections.api/fetch-warehouse-meta {:object :schemas :catalog catalog}]))

       ;; return unmount callback
       (fn [] (dispatch [:warehouse-meta/delete])))
     #js [])

    (react/useEffect
     (fn []
       (when (.-current init)
         (set-schema nil)
         (dispatch [:connections.api/fetch-warehouse-meta
                    {:object  :schemas
                     :catalog catalog}]))
       js/undefined)
     #js [catalog])

    (react/useEffect
     (fn []
       (field-action catalog schema table)
       js/undefined)
     #js [catalog schema table])

    (react/useEffect
     (fn []
       (set! (.-current init) true)
       js/undefined)
     #js [])

    (let [loading?        loading
          catalogs        (maybe-add-existing-option catalogs catalog)
          schemas         (maybe-add-existing-option schemas schema)
          empty-catalogs? (empty? catalogs)
          empty-schemas?  (empty? schemas)]
      (if (and loading? (:inline-loader block))
        [:div {:style {:height "48px" :margin-top "16px" :margin-bottom "8px" :display "flex" :align-items "flex-end"}}
         [:span {:style {:margin-right "10px"}}
          [components.spinner/inline-configurable {:color "primary" :size 16}]]
         loading-text]

        [:div {:class "w-full flex flex-col gap-4"}
         [(if (> (count catalogs) 10) ui.fields/searchable-select ui.fields/select)
          {:field         {:value  catalog
                           :action set-catalog}
           :errors        errors
           :variant       "standard"
           :id            :warehouse-catalog
           :data-cy       :warehouse-input-catalog
           :required      true
           :native        false
           :searchable    true
           :options       (mapv convert-select-option catalogs)
           :disabled      (or loading? empty-catalogs?)
           :label         (cond
                            loading? loading-text
                            empty-catalogs? empty-catalogs-text
                            :else "Database")}]

         [(if (> (count schemas) 10) ui.fields/searchable-select ui.fields/select)
          {:field         {:value  schema
                           :action set-schema}
           :errors        errors
           :variant       "standard"
           :id            :warehouse-schema
           :data-cy       :warehouse-input-schema
           :required      true
           :native        false
           :searchable    true
           :options       (mapv convert-select-option schemas)
           :disabled      (or loading? empty-schemas?)
           :label         (cond
                            loading? loading-text
                            empty-schemas? empty-schemas-text
                            :else "Schema")}]

         [ui.fields/input
          {:field       {:value  table
                         :action #(set-table (js-interop/get-in % [:target :value]))}
           :errors      errors
           :data-cy     :warehouse-input-table
           :label       "Table"
           :placeholder "warehouse table name"
           :variant     "standard"
           :required    true
           :disabled    loading?}]]))))


(defn destination-select [props]
  [:f> destination-select'
   (assoc props
          :default-catalog @(re-posh/subscribe [:connections.subs/warehouse-db-name])
          :loading  @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-warehouse-meta])
          :catalogs @(re-posh/subscribe [:connections.subs/warehouse-meta :catalogs])
          :schemas  @(re-posh/subscribe [:connections.subs/warehouse-meta :schemas]))])


(defn database-select' [{:keys [block field errors loading catalogs]}]
  (let [{:keys [loading-text empty-catalogs-text]
         :or   {loading-text        "Loading"
                empty-catalogs-text "No databases to select"}} block
        init                    (react/useRef false)]

    ;; Initial setup

    (react/useEffect
     (fn []
       (dispatch [:warehouse-meta/add {}])
       (dispatch [:connections.api/fetch-warehouse-meta {:object :catalogs}])
       ;; return unmount callback
       (fn [] (dispatch [:warehouse-meta/delete])))
     #js [])

    (react/useEffect
     (fn []
       (set! (.-current init) true)
       js/undefined)
     #js [])

    (let [loading?        loading
          catalogs        (maybe-add-existing-option catalogs (:value field))
          empty-catalogs? (empty? catalogs)]
      (if (and loading? (:inline-loader block))
        [:div {:style {:height "48px" :margin-top "16px" :margin-bottom "8px" :display "flex" :align-items "flex-end"}}
         [:span {:style {:margin-right "10px"}}
          [components.spinner/inline-configurable {:color "primary" :size 16}]]
         loading-text]

        [:div {:class "w-full flex flex-col gap-4"}
         [ui.fields/select
          {:field         field
           :errors        errors
           :variant       "standard"
           :id            :warehouse-catalog
           :data-cy       :warehouse-input-catalog
           :required      true
           :native        false
           :searchable    true
           :options       (mapv convert-select-option catalogs)
           :disabled      (or loading? empty-catalogs?)
           :label         (cond
                            loading? loading-text
                            empty-catalogs? empty-catalogs-text
                            :else "Database")}]]))))


(defn database-select [props]
  [:f> database-select'
   (assoc props
          :default-catalog @(re-posh/subscribe [:connections.subs/warehouse-db-name])
          :loading  @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-warehouse-meta])
          :catalogs @(re-posh/subscribe [:connections.subs/warehouse-meta :catalogs]))])
